import React, { useRef, useEffect, useImperativeHandle, forwardRef } from 'react'
import { smartQuotesMd } from '../lib/typography'
import { ensureBlockBreaks } from '../lib/text'

function mdToHtml(md) {
  return ensureBlockBreaks(md)
    // Normalize single newlines within paragraphs to spaces
    .replace(/([^\n])\n([^\n])/g, '$1 $2')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" style="color:#B33F1F">$1</a>')
    .replace(/\n/g, '<br>')
}

function htmlToMd(html) {
  return (html || '')
    .replace(/<strong>(.*?)<\/strong>/gi, '**$1**')
    .replace(/<em>(.*?)<\/em>/gi, '*$1*')
    .replace(/<a[^>]+href="([^"]+)"[^>]*>(.*?)<\/a>/gi, '[$2]($1)')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<div>/gi, '\n')
    .replace(/<\/div>/gi, '')
    .replace(/<[^>]+>/g, '')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&nbsp;/g, ' ')
}

const RichEditor = forwardRef(function RichEditor({ value, onChange, placeholder, className }, ref) {
  const divRef = useRef(null)
  const isComposing = useRef(false)
  const lastMd = useRef(value || '')

  // Set initial content on mount, then sync only when value changes externally
  useEffect(() => {
    if (!divRef.current) return
    const html = mdToHtml(value || '')
    divRef.current.innerHTML = html
    lastMd.current = value || ''
  }, []) // eslint-disable-line

  // Sync when value changes externally (e.g. AI draft arriving)
  useEffect(() => {
    if (!divRef.current) return
    if (value !== lastMd.current) {
      divRef.current.innerHTML = mdToHtml(value || '')
      lastMd.current = value || ''
    }
  }, [value])

  function handleInput() {
    if (!divRef.current || isComposing.current) return
    const md = smartQuotesMd(htmlToMd(divRef.current.innerHTML))
    lastMd.current = md
    onChange(md)
  }

  function applyFormat(tag) {
    divRef.current?.focus()
    const sel = window.getSelection()
    if (!sel || sel.rangeCount === 0) return
    const range = sel.getRangeAt(0)
    const selected = range.toString()
    let node
    if (tag === 'bold') {
      node = document.createElement('strong')
      node.textContent = selected || 'bold text'
    } else if (tag === 'italic') {
      node = document.createElement('em')
      node.textContent = selected || 'italic text'
    } else if (tag === 'link') {
      const url = window.prompt('Enter URL:')
      if (!url) return
      node = document.createElement('a')
      node.href = url
      node.style.color = '#B33F1F'
      node.textContent = selected || 'link text'
    }
    if (node) {
      range.deleteContents()
      range.insertNode(node)
      range.setStartAfter(node)
      range.collapse(true)
      sel.removeAllRanges()
      sel.addRange(range)
      handleInput()
    }
  }

  function handleKeyDown(e) {
    if (e.metaKey || e.ctrlKey) {
      if (e.key === 'b') { e.preventDefault(); applyFormat('bold') }
      if (e.key === 'i') { e.preventDefault(); applyFormat('italic') }
      if (e.key === 'k') { e.preventDefault(); applyFormat('link') }
    }
  }

  useImperativeHandle(ref, () => ({ applyFormat }))

  const isEmpty = !value || value.trim() === ''

  return (
    <div style={{ position: 'relative' }}>
      {isEmpty && <div className="rich-editor-placeholder">{placeholder}</div>}
      <div
        ref={divRef}
        contentEditable
        suppressContentEditableWarning
        className={className || 'rich-editor'}
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        onCompositionStart={() => { isComposing.current = true }}
        onCompositionEnd={() => { isComposing.current = false; handleInput() }}
      />
    </div>
  )
})

export default RichEditor

export function applyFormatToEditor(ref, tag) {
  ref.current?.applyFormat?.(tag)
}
